import { useCallback, useRef, useState } from 'react';

type UseOnlySerializeFirstBatchProps = {
	adfContentId?: string;
};

export const useOnlySerializeFirstBatch = ({
	adfContentId,
}: UseOnlySerializeFirstBatchProps): {
	onlySerializeFirstBatch: boolean;
	onBatchRendered: (done: boolean) => void;
} => {
	// adfContentIdSequenceRef is set to a new object every time adfContentId changes. This allows us to
	// distinguish between separate serializations of the same contentId. For example, when the user
	// navigates away from a page and then back to it.
	const adfContentIdSequenceRef = useRef<{ adfContentId: string } | undefined>(undefined);
	if (adfContentIdSequenceRef.current?.adfContentId !== adfContentId) {
		adfContentIdSequenceRef.current = adfContentId ? { adfContentId } : undefined;
	}

	// fullySerializeAdfContentIdSequence tracks the most recently batch rendered content. If adfContentIdSequenceRef matches this,
	// then we know that the current content should now be fully serialized.
	const [fullySerializeAdfContentIdSequence, setFullySerializeAdfContentIdSequence] =
		useState<typeof adfContentIdSequenceRef.current>(undefined);
	// We are intentionally using referential equality here. This is because we want to distinguish between
	// separate serializations of the same contentId.
	const fullySerialize = adfContentIdSequenceRef.current === fullySerializeAdfContentIdSequence;

	const onBatchRendered = useCallback(
		(done: boolean): void => {
			if (!done) {
				setFullySerializeAdfContentIdSequence(adfContentIdSequenceRef.current);
			}
		},
		[setFullySerializeAdfContentIdSequence],
	);

	return {
		onlySerializeFirstBatch: !fullySerialize,
		onBatchRendered,
	};
};
